import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Heading,
  Flex,
  IconButton,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Select,
  Spinner,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import ChatInterface from "./ChatInterface";
import QueryExecutor from "./QueryExecutor";

interface QueryListingPageProps {
  onClose: () => void;
}

interface ChatItem {
  searchResultId: number;
  generatedQuery: string;
  executionCount: number;
  createdOn: string;
  createdBy: string;
}

interface ChatResponse {
  data: ChatItem[];
  pageNumber: number;
  pageSize: number;
  totalRecords: number;
}
const Base_apiUrl=process.env.REACT_APP_API_Base_Url;
const QueryListingPage: React.FC<QueryListingPageProps> = ({ onClose }) => {
  const bgColor = useColorModeValue("white", "gray.800");

  const [chatItems, setChatItems] = useState<ChatItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showQueryExecutorPage, setShowQueryExecutorPage] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState<string | null>(null);
  const [showQueryListingPage, setShowQueryListingPage] = useState(false);
  const totalPages = Math.ceil(totalRecords / itemsPerPage);

  useEffect(() => {
    fetchChatList();
  }, [currentPage, itemsPerPage]);

  const fetchChatList = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${Base_apiUrl}Chat/GetChatList?PageNumber=${currentPage}&PageSize=${itemsPerPage}`,
        {
          headers: {
            accept: "text/plain",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ChatResponse = await response.json();
      setChatItems(data.data);
      setTotalRecords(data.totalRecords);
    } catch (err) {
      console.error("Error fetching chat list:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch chat list"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleExecute = (executeQuery: string) => {
    setSelectedQuery(executeQuery);
    setShowQueryExecutorPage(true);
  };

  const handleCloseExecutor = () => {
    setShowQueryExecutorPage(false);
    setSelectedQuery(null);
  };
  if (showQueryListingPage) {
    return <ChatInterface onClose={() => setShowQueryListingPage(false)} />;
  }
  // Show QueryExecutor page only
  if (showQueryExecutorPage && selectedQuery) {
    return (
      <Box px={{ base: 2, md: 4 }} py={4}>
        <Box
          w="100%"
          maxW="1050px"
          mx="auto"
          borderRadius="lg"
          boxShadow="md"
          bg={bgColor}
          p={4}
        >
          <Button onClick={handleCloseExecutor} colorScheme="gray" mb={4}>
            ← Back to Listing
          </Button>
          <QueryExecutor
            encodedQuery={selectedQuery}
            shouldExecute={true}
            onClose={handleCloseExecutor}
          />
        </Box>
      </Box>
    );
  }

  // Show query listing page
  return (
    <Box px={{ base: 2, md: 4 }} py={4}>
      <Box
        w="100%"
        maxW="1050px"
        mx="auto"
        borderRadius="lg"
        boxShadow="md"
        overflow="hidden"
        bg={bgColor}
      >
        <Box p={{ base: 3, md: 4 }} borderBottomWidth="1px">
          <Heading size={{ base: "sm", md: "md" }}>Query Management</Heading>
        </Box>

        <Box p={{ base: 3, md: 4 }}>
          {isLoading ? (
            <Flex justify="center" align="center" p={8}>
              <Spinner size="xl" />
            </Flex>
          ) : error ? (
            <Text color="red.500" textAlign="center" p={4}>
              Error: {error}
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple" size={{ base: "sm", md: "md" }}>
                <Thead>
                  <Tr>
                    <Th>Query</Th>
                    <Th>Count</Th>
                    <Th>Created On</Th>
                    <Th>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {chatItems.length > 0 ? (
                    chatItems.map((item) => (
                      <Tr key={item.searchResultId}>
                        <Td>{item.generatedQuery}</Td>
                        <Td>{item.executionCount}</Td>
                        <Td>
                          {new Date(item.createdOn)
                            .toLocaleDateString("en-GB")
                            .replaceAll("/", "-")}
                        </Td>
                        <Td>
                          <Button
                            colorScheme="blue"
                            size="sm"
                            onClick={() => handleExecute(item.generatedQuery)}
                          >
                            Execute
                          </Button>
                        </Td>
                      </Tr>
                    ))
                  ) : (
                    <Tr>
                      <Td colSpan={4} textAlign="center">
                        No records found
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          )}

          <Flex
            direction={{ base: "column", md: "row" }}
            justifyContent="space-between"
            mt={5}
            alignItems={{ base: "stretch", md: "center" }}
            gap={3}
          >
            <Text textAlign={{ base: "center", md: "left" }}>
              Page {currentPage} of {totalPages || 1}
            </Text>

            <Flex justifyContent="center">
              <IconButton
                aria-label="Previous page"
                icon={<ChevronLeftIcon />}
                onClick={goToPrevPage}
                isDisabled={currentPage === 1 || isLoading}
                mr={2}
                size="sm"
              />
              <IconButton
                aria-label="Next page"
                icon={<ChevronRightIcon />}
                onClick={goToNextPage}
                isDisabled={
                  currentPage === totalPages || totalPages === 0 || isLoading
                }
                size="sm"
              />
            </Flex>

            <Select
              width={{ base: "100%", md: "100px" }}
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              isDisabled={isLoading}
              size="sm"
            >
              <option value="3">3</option>
              <option value="5">5</option>
              <option value="10">10</option>
            </Select>
          </Flex>
        </Box>
      </Box>

      <Text fontSize="sm" color="gray.600" textAlign="center">
        <Button
          variant="link"
          colorScheme="blue"
          onClick={() => setShowQueryListingPage(true)}
        >
          Back to chat interface
        </Button>
      </Text>
    </Box>
  );
};

export default QueryListingPage;
